import { UserSettings } from '@xeetr/types/models'
import { useCallback, useContext, useMemo } from 'react'

import { useUserSettings } from './useUserSettings'
import { ActiveTenantContext } from '@/context/common/Context'

export type ColumnType = 'dashboard' | 'customer'

interface UseColumnOrderResult {
  orderedColumns: string[]
  updateColumnOrder: (newOrder: string[]) => void
  isLoading: boolean
}

/**
 * Hook for managing column order persistence
 * Reorders columns based on user settings and persists changes
 *
 * @param columns - The original column definitions
 * @param columnType - The type of columns ('dashboard' or 'customer')
 * @returns Ordered columns and update function
 */
export const useColumnOrder = (columnType: ColumnType): UseColumnOrderResult => {
  const { settings, updateSettings, isLoading, isUpdating } = useUserSettings()
  const { tenantId } = useContext(ActiveTenantContext)

  // Get column order from settings
  const orderedColumns = useMemo(() => {
    if (!tenantId) return []
    const orderKey = columnType === 'dashboard' ? 'dashboardColumnOrder' : 'customerColumnOrder'
    return settings[orderKey]?.[tenantId] ?? []
  }, [tenantId, settings, columnType])

  // Update column order in user settings
  const updateColumnOrder = useCallback(
    (newOrder: string[]) => {
      if (!tenantId) return

      const orderKey = columnType === 'dashboard' ? 'dashboardColumnOrder' : 'customerColumnOrder'

      updateSettings({
        [orderKey]: {
          ...(settings[orderKey] as Record<string, string[]> | undefined),
          [tenantId]: newOrder,
        },
      } as Partial<UserSettings>)
    },
    [tenantId, columnType, settings, updateSettings]
  )

  return {
    orderedColumns,
    updateColumnOrder,
    isLoading: isLoading || isUpdating,
  }
}
